import { createApi } from "@reduxjs/toolkit/query/react";
import { mockBaseQuery, ListResponse } from "./mock/baseQuery";
import type {
  AcademicYear,
  Chapter,
  ChapterTest,
  Note,
  Question,
  Student,
  Subject,
  Video,
  WeeklyExam,
} from "./mock/db";

export type ListParams = {
  search?: string;
  sortBy?: string;
  order?: "asc" | "desc";
  page?: number;
  limit?: number;
  [key: string]: any;
};

export const api = createApi({
  reducerPath: "api",
  baseQuery: mockBaseQuery,
  tagTypes: [
    "Year",
    "Subject",
    "Chapter",
    "Student",
    "Video",
    "Note",
    "ChapterTest",
    "WeeklyExam",
    "Question",
  ],
  endpoints: (b) => ({
    // ----- Years -----
    getYears: b.query<ListResponse<AcademicYear>, ListParams>({
      query: (params = {}) => ({ url: "/years", params }),
      providesTags: ["Year"],
    }),
    addYear: b.mutation<AcademicYear, Partial<AcademicYear>>({
      query: (body) => ({ url: "/years", method: "POST", body }),
      invalidatesTags: ["Year"],
    }),
    updateYear: b.mutation<AcademicYear, { id: string; body: Partial<AcademicYear> }>({
      query: ({ id, body }) => ({ url: `/years/${id}`, method: "PATCH", body }),
      invalidatesTags: ["Year"],
    }),
    deleteYear: b.mutation<AcademicYear, string>({
      query: (id) => ({ url: `/years/${id}`, method: "DELETE" }),
      invalidatesTags: ["Year"],
    }),

    // ----- Subjects -----
    getSubjects: b.query<ListResponse<Subject>, ListParams>({
      query: (params = {}) => ({ url: "/subjects", params }),
      providesTags: ["Subject"],
    }),
    addSubject: b.mutation<Subject, Partial<Subject>>({
      query: (body) => ({ url: "/subjects", method: "POST", body }),
      invalidatesTags: ["Subject"],
    }),
    updateSubject: b.mutation<Subject, { id: string; body: Partial<Subject> }>({
      query: ({ id, body }) => ({ url: `/subjects/${id}`, method: "PATCH", body }),
      invalidatesTags: ["Subject"],
    }),
    deleteSubject: b.mutation<Subject, string>({
      query: (id) => ({ url: `/subjects/${id}`, method: "DELETE" }),
      invalidatesTags: ["Subject"],
    }),

    // ----- Chapters -----
    getChapters: b.query<ListResponse<Chapter>, ListParams>({
      query: (params = {}) => ({ url: "/chapters", params }),
      providesTags: ["Chapter"],
    }),
    addChapter: b.mutation<Chapter, Partial<Chapter>>({
      query: (body) => ({ url: "/chapters", method: "POST", body }),
      invalidatesTags: ["Chapter"],
    }),
    updateChapter: b.mutation<Chapter, { id: string; body: Partial<Chapter> }>({
      query: ({ id, body }) => ({ url: `/chapters/${id}`, method: "PATCH", body }),
      invalidatesTags: ["Chapter"],
    }),
    deleteChapter: b.mutation<Chapter, string>({
      query: (id) => ({ url: `/chapters/${id}`, method: "DELETE" }),
      invalidatesTags: ["Chapter"],
    }),

    // ----- Students -----
    getStudents: b.query<ListResponse<Student>, ListParams>({
      query: (params = {}) => ({ url: "/students", params }),
      providesTags: ["Student"],
    }),
    addStudent: b.mutation<Student, Partial<Student>>({
      query: (body) => ({ url: "/students", method: "POST", body }),
      invalidatesTags: ["Student"],
    }),
    updateStudent: b.mutation<Student, { id: string; body: Partial<Student> }>({
      query: ({ id, body }) => ({ url: `/students/${id}`, method: "PATCH", body }),
      invalidatesTags: ["Student"],
    }),
    deleteStudent: b.mutation<Student, string>({
      query: (id) => ({ url: `/students/${id}`, method: "DELETE" }),
      invalidatesTags: ["Student"],
    }),

    // ----- Videos -----
    getVideos: b.query<ListResponse<Video>, ListParams>({
      query: (params = {}) => ({ url: "/videos", params }),
      providesTags: ["Video"],
    }),
    addVideo: b.mutation<Video, Partial<Video>>({
      query: (body) => ({ url: "/videos", method: "POST", body }),
      invalidatesTags: ["Video"],
    }),
    updateVideo: b.mutation<Video, { id: string; body: Partial<Video> }>({
      query: ({ id, body }) => ({ url: `/videos/${id}`, method: "PATCH", body }),
      invalidatesTags: ["Video"],
    }),
    deleteVideo: b.mutation<Video, string>({
      query: (id) => ({ url: `/videos/${id}`, method: "DELETE" }),
      invalidatesTags: ["Video"],
    }),

    // ----- Notes -----
    getNotes: b.query<ListResponse<Note>, ListParams>({
      query: (params = {}) => ({ url: "/notes", params }),
      providesTags: ["Note"],
    }),
    addNote: b.mutation<Note, Partial<Note>>({
      query: (body) => ({ url: "/notes", method: "POST", body }),
      invalidatesTags: ["Note"],
    }),
    updateNote: b.mutation<Note, { id: string; body: Partial<Note> }>({
      query: ({ id, body }) => ({ url: `/notes/${id}`, method: "PATCH", body }),
      invalidatesTags: ["Note"],
    }),
    deleteNote: b.mutation<Note, string>({
      query: (id) => ({ url: `/notes/${id}`, method: "DELETE" }),
      invalidatesTags: ["Note"],
    }),

    // ----- Chapter Tests -----
    getChapterTests: b.query<ListResponse<ChapterTest>, ListParams>({
      query: (params = {}) => ({ url: "/chapterTests", params }),
      providesTags: ["ChapterTest"],
    }),
    addChapterTest: b.mutation<ChapterTest, Partial<ChapterTest>>({
      query: (body) => ({ url: "/chapterTests", method: "POST", body }),
      invalidatesTags: ["ChapterTest"],
    }),
    updateChapterTest: b.mutation<ChapterTest, { id: string; body: Partial<ChapterTest> }>({
      query: ({ id, body }) => ({ url: `/chapterTests/${id}`, method: "PATCH", body }),
      invalidatesTags: ["ChapterTest"],
    }),
    deleteChapterTest: b.mutation<ChapterTest, string>({
      query: (id) => ({ url: `/chapterTests/${id}`, method: "DELETE" }),
      invalidatesTags: ["ChapterTest"],
    }),

    // ----- Weekly Exams -----
    getWeeklyExams: b.query<ListResponse<WeeklyExam>, ListParams>({
      query: (params = {}) => ({ url: "/weeklyExams", params }),
      providesTags: ["WeeklyExam"],
    }),
    addWeeklyExam: b.mutation<WeeklyExam, Partial<WeeklyExam>>({
      query: (body) => ({ url: "/weeklyExams", method: "POST", body }),
      invalidatesTags: ["WeeklyExam"],
    }),
    updateWeeklyExam: b.mutation<WeeklyExam, { id: string; body: Partial<WeeklyExam> }>({
      query: ({ id, body }) => ({ url: `/weeklyExams/${id}`, method: "PATCH", body }),
      invalidatesTags: ["WeeklyExam"],
    }),
    deleteWeeklyExam: b.mutation<WeeklyExam, string>({
      query: (id) => ({ url: `/weeklyExams/${id}`, method: "DELETE" }),
      invalidatesTags: ["WeeklyExam"],
    }),

    // ----- Questions -----
    getQuestions: b.query<ListResponse<Question>, ListParams>({
      query: (params = {}) => ({ url: "/questions", params }),
      providesTags: ["Question"],
    }),
    addQuestion: b.mutation<Question, Partial<Question>>({
      query: (body) => ({ url: "/questions", method: "POST", body }),
      invalidatesTags: ["Question"],
    }),
    updateQuestion: b.mutation<Question, { id: string; body: Partial<Question> }>({
      query: ({ id, body }) => ({ url: `/questions/${id}`, method: "PATCH", body }),
      invalidatesTags: ["Question"],
    }),
    deleteQuestion: b.mutation<Question, string>({
      query: (id) => ({ url: `/questions/${id}`, method: "DELETE" }),
      invalidatesTags: ["Question"],
    }),
  }),
});

export const {
  useGetYearsQuery,
  useAddYearMutation,
  useUpdateYearMutation,
  useDeleteYearMutation,
  useGetSubjectsQuery,
  useAddSubjectMutation,
  useUpdateSubjectMutation,
  useDeleteSubjectMutation,
  useGetChaptersQuery,
  useAddChapterMutation,
  useUpdateChapterMutation,
  useDeleteChapterMutation,
  useGetStudentsQuery,
  useAddStudentMutation,
  useUpdateStudentMutation,
  useDeleteStudentMutation,
  useGetVideosQuery,
  useAddVideoMutation,
  useUpdateVideoMutation,
  useDeleteVideoMutation,
  useGetNotesQuery,
  useAddNoteMutation,
  useUpdateNoteMutation,
  useDeleteNoteMutation,
  useGetChapterTestsQuery,
  useAddChapterTestMutation,
  useUpdateChapterTestMutation,
  useDeleteChapterTestMutation,
  useGetWeeklyExamsQuery,
  useAddWeeklyExamMutation,
  useUpdateWeeklyExamMutation,
  useDeleteWeeklyExamMutation,
  useGetQuestionsQuery,
  useAddQuestionMutation,
  useUpdateQuestionMutation,
  useDeleteQuestionMutation,
} = api;
